import { Injectable, inject, signal } from '@angular/core';
import { ICliente, IUsuario } from 'modelos/src';
import { ClienteSelectorCacheService } from '../../../../componentes/selectores/cliente-selector/cliente-selector-cache.service';
import { UsuariosService } from '../../../../auxiliares/servicios/http/usuarios.service';
import { LoginService } from '../../../login/login.service';
import { enTramos } from './seguimiento.helpers';

/**
 * Nombres que muestra Seguimiento y que no vienen en los datos: de qué cliente
 * es cada acción y quién la hizo.
 *
 * Los clientes salen del caché del selector de clientes (el mismo que usa el
 * resto de IRIX), así no se piden de nuevo en cada pantalla.
 */
@Injectable({ providedIn: 'root' })
export class NombresSeguimientoService {
  private cacheClientes = inject(ClienteSelectorCacheService);
  private usuariosService = inject(UsuariosService);

  private readonly clientes = signal<Map<string, ICliente>>(new Map());

  constructor() {
    // Se pide al crear el servicio y no al leer un nombre: los nombres se leen
    // dentro de `computed()`, donde no se puede escribir una signal.
    this.cacheClientes.getClientes().subscribe({
      next: (lista) => this.clientes.set(new Map(lista.map((c) => [c._id, c]))),
      error: () => undefined,
    });
  }

  /** Nombre del cliente; vacío mientras no llegó el caché o si no se ve. */
  public nombreCliente(id: string | undefined): string {
    if (!id) return '';
    const c = this.clientes().get(id);
    if (c?.nombre) return c.nombre;
    const propio = LoginService.getCliente();
    return propio?._id === id ? propio.nombre || '' : '';
  }

  /**
   * La cadena de clientes desde el propio hasta éste, para el tooltip: con
   * clientes homónimos el nombre solo no alcanza.
   */
  public jerarquiaCliente(id: string | undefined): string {
    if (!id) return '';
    const mapa = this.clientes();
    const propio = LoginService.getCliente();
    const cadena: string[] = [];
    const vistos = new Set<string>();
    let actual = mapa.get(id);
    while (actual?._id && !vistos.has(actual._id)) {
      vistos.add(actual._id);
      cadena.unshift(actual.nombre || 'Sin nombre');
      if (actual._id === propio?._id) break;
      actual = actual.idPadre ? mapa.get(actual.idPadre) : undefined;
    }
    return cadena.length > 1 ? cadena.join(' › ') : '';
  }

  /** Nombres de usuario por id, pedidos de a tramos. */
  public async usuarios(ids: string[]): Promise<Map<string, IUsuario>> {
    const mapa = new Map<string, IUsuario>();
    for (const tramo of enTramos(ids)) {
      const res = await this.usuariosService
        .getFiltered({
          filter: JSON.stringify({ _id: { $in: tramo } }),
          select: '_id usuario datosPersonales.nombre',
          limit: tramo.length,
          excludeTotalCount: true,
        })
        .catch((): undefined => undefined);
      for (const u of res?.datos ?? []) if (u._id) mapa.set(u._id, u);
    }
    return mapa;
  }
}

/** "Nombre" del usuario, o su usuario si no tiene nombre. */
export function nombreUsuario(u: IUsuario | undefined): string | undefined {
  return u?.datosPersonales?.nombre || u?.usuario || undefined;
}
